import { useState, useEffect, useRef } from "react";
import useLearningPlatform from "../../../api/learningPlatform";
import { Upload, Trash2, Loader2, Check } from "lucide-react";
import {
  FormField,
  ColorPicker,
  inputClass,
  buttonClass,
  SectionLoader,
  isNotFoundError,
} from "./shared";

const DEFAULT_SETTINGS = {
  platform_name: "",
  primary_color: "#1E4FCF",
  accent_color: "#4d7ee0",
  logo: "",
  logoUrl: "",
};

const BrandingTab = ({ isDark }) => {
  const { getSettings, updateSettings } = useLearningPlatform();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [original, setOriginal] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        const merged = { ...DEFAULT_SETTINGS, ...(data || {}) };
        setSettings(merged);
        setOriginal(merged);
      } catch (e) {
        if (!isNotFoundError(e)) setError("Kon instellingen niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getSettings]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(original);
  const update = (patch) => { setSettings((p) => ({ ...p, ...patch })); setSavedAt(null); };
  const handleCancel = () => { setSettings(original); setError(null); setSavedAt(null); };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Selecteer een afbeelding"); return; }
    const reader = new FileReader();
    reader.onload = () => update({ logo: reader.result });
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await updateSettings(settings);
      const merged = { ...DEFAULT_SETTINGS, ...(data || settings) };
      setSettings(merged);
      setOriginal(merged);
      setSavedAt(Date.now());
    } catch (e) {
      setError("Kon niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  const logoSrc = settings.logo?.startsWith("data:") || settings.logo?.startsWith("http")
    ? settings.logo
    : (settings.logoUrl || null);

  return (
    <div className="space-y-6 max-w-md">
      <FormField label="Naam van platform" isDark={isDark}>
        <input
          type="text"
          value={settings.platform_name}
          onChange={(e) => update({ platform_name: e.target.value })}
          placeholder="bv. Academy by Acme"
          className={inputClass(isDark)}
        />
      </FormField>

      <FormField label="Logo" isDark={isDark}>
        <div className="flex items-center gap-3">
          <div className={`h-16 w-16 rounded-lg overflow-hidden flex items-center justify-center border flex-shrink-0 ${isDark ? "bg-white/[0.04] border-white/[0.08]" : "bg-gray-50 border-gray-200"}`}>
            {logoSrc
              ? <img src={logoSrc} alt="Logo" className="h-full w-full object-contain" />
              : <Upload size={18} className={isDark ? "text-gray-600" : "text-gray-400"} />}
          </div>
          <div className="flex flex-col gap-1.5">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} />
            <button type="button" onClick={() => fileInputRef.current?.click()} className={buttonClass(isDark, "secondary")}>
              <Upload size={13} /> {logoSrc ? "Vervangen" : "Uploaden"}
            </button>
            {logoSrc && (
              <button type="button" onClick={() => update({ logo: "", logoUrl: "" })} className={`text-[11px] flex items-center gap-1 transition-colors ${isDark ? "text-gray-500 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}>
                <Trash2 size={11} /> Verwijderen
              </button>
            )}
          </div>
        </div>
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Hoofdkleur" isDark={isDark}>
          <ColorPicker value={settings.primary_color} onChange={(v) => update({ primary_color: v })} isDark={isDark} />
        </FormField>
        <FormField label="Accentkleur" isDark={isDark}>
          <ColorPicker value={settings.accent_color} onChange={(v) => update({ accent_color: v })} isDark={isDark} />
        </FormField>
      </div>

      {error && (
        <div className={`text-[12px] px-3 py-2 rounded-md ${isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"}`}>
          {error}
        </div>
      )}

      <div className={`flex items-center justify-end gap-2 pt-2 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}>
        {savedAt && !dirty && (
          <span className={`flex items-center gap-1 text-[12px] mr-auto ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
            <Check size={13} /> Opgeslagen
          </span>
        )}
        <button type="button" onClick={handleCancel} disabled={!dirty || saving} className={buttonClass(isDark, "ghost")}>
          Annuleer
        </button>
        <button type="button" onClick={handleSave} disabled={!dirty || saving} className={buttonClass(isDark, "primary")}>
          {saving && <Loader2 size={13} className="animate-spin" />}
          Opslaan
        </button>
      </div>
    </div>
  );
};

export default BrandingTab;
